import { KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "../../../../utils/styles";
import { CountryList } from "./styles";

// Most of Keepo's buyers: on top of the list, before the alphabetical rest
const PRIORITY = ["BR", "PT", "US"];

const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** The country's flag as an emoji, from its two letters (Windows shows the letters instead). */
const flagOf = (code: string) => String.fromCodePoint(...[...code].map(letter => 0x1f1a5 + letter.charCodeAt(0)));

type Country = { code: string; name: string };

type Props = {
  value: string;
  onChange: (code: string) => void;
  countries: Country[];
};

/** The country field: typing filters the list (by name or code, accents ignored), arrows and Enter pick one. */
export const CountryCombobox = ({ value, onChange, countries }: Props) => {
  const { t } = useTranslation();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const sorted = useMemo(
    () => [
      ...PRIORITY.map(code => countries.find(country => country.code === code)).filter(
        (country): country is Country => !!country
      ),
      ...countries.filter(country => !PRIORITY.includes(country.code)),
    ],
    [countries]
  );

  const filtered = useMemo(() => {
    const search = normalize(query.trim());
    return search
      ? sorted.filter(({ code, name }) => normalize(name).includes(search) || code.toLowerCase() === search)
      : sorted;
  }, [query, sorted]);

  const selected = countries.find(country => country.code === value);

  // Keeps the highlighted option in sight while moving with the arrows
  useEffect(() => {
    if (isOpen) {
      listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
    }
  }, [active, isOpen]);

  const open = () => {
    setQuery("");
    setActive(
      Math.max(
        0,
        sorted.findIndex(country => country.code === value)
      )
    );
    setOpen(true);
  };

  const pick = (code: string) => {
    onChange(code);
    setOpen(false);
    inputRef.current?.blur();
  };

  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (event.key === "ArrowDown" || event.key === "Enter")) {
      event.preventDefault();
      open();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(Math.min(active + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(active - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (filtered[active]) pick(filtered[active].code);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <>
      {selected && !isOpen && (
        <span className="flag" aria-hidden="true">
          {flagOf(selected.code)}
        </span>
      )}
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={isOpen && filtered[active] ? `${listId}-${filtered[active].code}` : undefined}
        autoComplete="off"
        // Closed, it shows the chosen country; open, it's the search
        value={isOpen ? query : selected?.name || ""}
        placeholder={isOpen ? t("pages.subscription.checkout.billing.searchCountry") : undefined}
        onFocus={open}
        onClick={() => !isOpen && open()}
        onBlur={() => setOpen(false)}
        onChange={event => {
          setQuery(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={keyDown}
      />
      {isOpen && (
        <CountryList id={listId} ref={listRef} role="listbox">
          {filtered.length ? (
            filtered.map(({ code, name }, index) => (
              <li
                key={code}
                id={`${listId}-${code}`}
                role="option"
                aria-selected={code === value}
                className={classNames({ active: index === active, divided: !query && index === PRIORITY.length })}
                // Before the input's blur, so the click lands
                onMouseDown={event => event.preventDefault()}
                onMouseEnter={() => setActive(index)}
                onClick={event => {
                  // The field sits in a <label>: without this the click would also focus it and reopen the list
                  event.preventDefault();
                  pick(code);
                }}>
                <span aria-hidden="true">{flagOf(code)}</span>
                {name}
              </li>
            ))
          ) : (
            <li className="empty">{t("pages.subscription.checkout.billing.noCountry")}</li>
          )}
        </CountryList>
      )}
    </>
  );
};
